import { createSlice } from "@reduxjs/toolkit";
import { logout } from "./actions";
import FetchStatus from "types/FetchStatus.enum";
import dayjs from "dayjs";

const initialState = {
  isEmptyRun: false,
  status: FetchStatus.idle,
  dataUpdatedAt: 0,
  isTagging: false,
  isRunLive: false,
  isZoomed: false,
  tooltip: [],
  runTypeSentAt: null,
  toleranceTag: null,
  time: {
    current: null,
    selected: null,
    pos: null,
    limits: {
      start: null,
      end: null
    }
  },
  settings: {
    showSelfTests: true,
    showAlarms: true,
    showStandBy: true
  },
  activeElement: null,
  tags: [],
  yAxisVisibility: true
};

export const graphSlice = createSlice({
  name: "graph",
  initialState,
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setIsEmptyRun: (state, action) => {
      state.isEmptyRun = action.payload;
    },
    setDataUpdatedAt: (state, action) => {
      state.dataUpdatedAt = Math.max(state.dataUpdatedAt, action.payload);
    },
    setCurrentTime: (state, action) => {
      if (!state.time.selected) {
        state.time.current = action.payload.time;
        state.time.pos = action.payload.pos;
      }
    },
    setSelectedTime: (state, action) => {
      state.time.selected = action.payload.time;
      state.time.pos = action.payload.pos;
    },
    setCurrentTags: (state, action) => {
      state.time.current = null;
      state.time.selected = null;
      state.isZoomed = false;
      if (action.payload.length === 0) {
        state.time.limits.start = null;
        state.time.limits.end = null;
      }
      state.tags = [...action.payload];
    },
    setIsRunLive: (state, action) => {
      state.isRunLive = action.payload;
    },
    setIsTagging: (state, action) => {
      state.isTagging = action.payload;
    },
    toggleAxisVisibility: (state) => {
      state.yAxisVisibility = !state.yAxisVisibility;
    },
    setTimeLimits: (state, action) => {
      if (state.tags.length === 0 || state.status === FetchStatus.fetching) {
        state.time.limits.start = null;
        state.time.limits.end = null;
      } else {
        state.time.limits.start = action.payload.min;
        state.time.limits.end = action.payload.max;
      }
    },
    setZoom: (state, action) => {
      state.isZoomed = action.payload;
    },
    setActiveElement: (state, action) => {
      state.activeElement = action.payload;
    },
    setRunTimeSentAt: (state, action) => {
      state.runTypeSentAt = dayjs(action.payload).valueOf();
    },
    setGraphSettings: (state, action) => {
      if (action.payload) {
        state.settings = { ...action.payload };
        state.time.selected = null;
      }
    },
    setTooltip: (state, action) => {
      state.tooltip = action.payload;
    },
    clearTime: (state) => {
      state.time.current = null;
      state.time.selected = null;
    },
    setToleranceTag: (state, action) => {
      state.toleranceTag = action.payload;
    },
    clearGraph: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  }
});

export const {
  setStatus,
  setIsEmptyRun,
  setDataUpdatedAt,
  setCurrentTime,
  setSelectedTime,
  setCurrentTags,
  setIsTagging,
  toggleAxisVisibility,
  clearTime,
  clearGraph,
  setTimeLimits,
  setTooltip,
  setZoom,
  setActiveElement,
  setIsRunLive,
  setGraphSettings,
  setRunTimeSentAt,
  setToleranceTag
} = graphSlice.actions;
export default graphSlice.reducer;
